import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../job.js';
import JobSchema from '../../../../models/job-schema.js';
import type Company from '../../../../models/company.js';
import definition from '@job-schemas/public-ledger/credit-extra-charge.json';
import { jobSchema } from '../../job-schema.js';
import { createLogger } from '../../../../ns-services/logger.js';
import { creditExtraChargeFigures, formatPence } from '../../../../helpers/credit-pricing.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';

const logger = createLogger('ns-public-ledger-credit-extra-charge');

/**
 * The PUBLIC_LEDGER team's card when a company paid twice for one nodeStream credits purchase (#25 DECISION 2.8): the
 * purchase keeps the payment it delivered, and the team refunds the extra payment by hand in Stripe, then presses
 * "Refunded".
 */
@customElement(NSPublicLedgerCreditExtraCharge.is)
export default class NSPublicLedgerCreditExtraCharge extends NSJobComponent {
  static get is() {
    return 'ns-public-ledger-credit-extra-charge';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { refunded: { type: 'button', label: 'Refunded' } },
      icon: 'transaction',
    });
  }

  static styles = [
    ...super.styles,
    css`
      .job-panel ns-detail-list {
        margin: 0 0 var(--wa-space-m);
        --ns-detail-list-gap: var(--wa-space-2xs);
      }
    `,
  ];

  @state()
  private __company?: Company;

  @state()
  private __busy = false;

  async connectedCallback() {
    await super.connectedCallback();
    const { companyId } = creditExtraChargeFigures(this._job?.metadata);
    if (!companyId || !this._db) return;
    try {
      this.__company = await this._db.getById<Company>('company', companyId);
    } catch (err) {
      logger.warn('Unable to load the company', err);
    }
  }

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const figures = creditExtraChargeFigures(this._job?.metadata);
    return html`
      <ns-job-layout>
        <p slot="intro">A company paid twice for one nodeStream credits purchase. Its credits were delivered once.</p>
        <div class="job-panel">
          <h3>The extra payment</h3>
          <ns-detail-list
            .rows=${[
              { label: 'Company', value: this.__company?.name ?? figures.companyId ?? undefined },
              {
                label: 'Amount',
                value: figures.amountPence !== null ? formatPence(figures.amountPence) : undefined,
              },
              { label: 'Stripe payment', value: figures.paymentIntentId ?? undefined },
              { label: 'Purchase', value: figures.purchaseId ?? undefined },
            ]}
          ></ns-detail-list>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>What to do</h3>
          <p>Refund this payment in full in Stripe, then press "Refunded".</p>
          <p>The purchase keeps the payment its credits were delivered for, so nothing else changes.</p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE' || !isUnclaimed(job)) return nothing;
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>Claim</wa-button>
    `;
  }
}
